import * as React from "react";
import { Button, Card } from "@jhool-io/fe-components";
import { AxiosError } from "axios";
import { Link } from "react-router-dom";
import ConsentStepper, {
    ConsentPaymentCardConfig,
} from "../../../client-forms/components/ConsentStepper/ConsentStepper";
import {
    FormStatus,
    IConsentFormAnswers,
} from "../../../client-forms/types/client-forms.types";
import {
    getConsentAttachmentFiles,
    isDraftSaveableStatus,
} from "../../../client-forms/helpers/client-forms.helpers";
import AddCardForm from "../../../payments/components/PaymentCards/AddCardForm";
import { useAcceptJS } from "../../../../hooks/useAcceptJS/useAcceptJS";
import useToast from "../../../../hooks/useToast";
import { CONSENT_LINK_SCOPES } from "../../constants/consent-link.constants";
import useConsentLinkSession from "../../hooks/useConsentLinkSession";
import {
    useScopedAddPaymentCard,
    useScopedConsentAttachments,
    useScopedConsentAction,
    useScopedConsentDraft,
} from "../../hooks/consent-link.mutations";
import {
    useScopedAuthorizeNetKeys,
    useScopedConsent,
    useScopedPaymentCardStatus,
} from "../../hooks/consent-link.queries";
import { downloadScopedConsentDocument } from "../../services/consent-link.services";
import { base64ToBlob } from "../../../../utils/helpers";

export default function ConsentLinkWrapper() {
    const session = useConsentLinkSession();
    const { toast } = useToast();
    const consentQuery = useScopedConsent(
        session.consentId,
        !session.loading && Boolean(session.token)
    );
    const action = useScopedConsentAction(session.consentId);
    const draft = useScopedConsentDraft(session.consentId);
    const addPaymentCard = useScopedAddPaymentCard(session.consentId);
    const uploadAttachments = useScopedConsentAttachments(session.consentId);
    const openedConsentId = React.useRef<string | null>(null);
    const consent = consentQuery.data?.data;
    const canUsePayment =
        session.hasScope(CONSENT_LINK_SCOPES.PAYMENT_CARD_STATUS) &&
        session.hasScope(CONSENT_LINK_SCOPES.PAYMENT_CARD_ADD);
    const paymentCardStatus = useScopedPaymentCardStatus(
        session.consentId,
        canUsePayment
    );
    const needsPaymentCredentials =
        canUsePayment && paymentCardStatus.data?.has_valid_card === false;
    const authorizeNetKeys = useScopedAuthorizeNetKeys(
        session.consentId,
        needsPaymentCredentials
    );
    const { isReady: isAcceptJsReady } = useAcceptJS({
        credentials: authorizeNetKeys.data,
        useDefaultCredentials: false,
    });

    React.useEffect(() => {
        if (
            consent &&
            !consent.opened_at &&
            openedConsentId.current !== consent.consent_id
        ) {
            openedConsentId.current = consent.consent_id;
            action.mutate({ action_type: "client_opened" });
        }
    }, [action, consent]);

    const queryEnabled = !session.loading && Boolean(session.token);
    if (session.loading || (queryEnabled && consentQuery.isLoading)) {
        return (
            <div className="page-loader">
                <span />
            </div>
        );
    }

    if (session.error || consentQuery.isError) {
        return (
            <div className="p-32">
                <Card classNames="p-24 w-full">
                    <p className="text-sm mb-16">
                        {session.error ?? "Could not load this consent."}
                    </p>
                    <Link
                        to="/login"
                        className="text-sm text-primary underline underline-offset-2"
                    >
                        Go to login
                    </Link>
                </Card>
            </div>
        );
    }

    if (!consent) {
        return (
            <div className="p-32">
                <Card classNames="p-24 w-full">
                    <p className="text-sm mb-16">
                        This consent is unavailable.
                    </p>
                    <Link
                        to="/login"
                        className="text-sm text-primary underline underline-offset-2"
                    >
                        Go to login
                    </Link>
                </Card>
            </div>
        );
    }

    const isDraftSaveable = isDraftSaveableStatus(consent.status);
    const submitted =
        !isDraftSaveable &&
        (consent.status === FormStatus.RECEIVED ||
            consent.status === FormStatus.COMPLETED ||
            consent.client_submitted);

    const submit = async (
        answers: IConsentFormAnswers,
        emailCopyRequested: boolean
    ) => {
        try {
            await action.mutateAsync({
                action_type: "client_submitted",
                email_copy_requested: emailCopyRequested,
                form_answers: answers,
            });
            toast({
                mode: "success",
                message: "Consent submitted successfully.",
            });
            consentQuery.refetch();
        } catch (error) {
            toast({
                mode: "error",
                message:
                    (error as AxiosError<{ message?: string }>).response?.data
                        ?.message ?? "Could not submit consent.",
            });
        }
    };

    const download = async (documentId: string, documentName: string) => {
        try {
            const file = await downloadScopedConsentDocument(documentId);
            const blob = base64ToBlob(file.data, file.content_type);
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = documentName || "consent-document";
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
            action.mutate({ action_type: "client_downloaded" });
        } catch {
            toast({
                mode: "error",
                message: "Could not download the consent document.",
            });
        }
    };

    let paymentCard: ConsentPaymentCardConfig | null = null;
    if (canUsePayment) {
        paymentCard = {
            hasValidCard: Boolean(paymentCardStatus.data?.has_valid_card),
            isLoading:
                paymentCardStatus.isLoading ||
                (needsPaymentCredentials && authorizeNetKeys.isLoading),
            isError:
                paymentCardStatus.isError ||
                (needsPaymentCredentials && authorizeNetKeys.isError),
            isFetching:
                paymentCardStatus.isFetching ||
                (needsPaymentCredentials && authorizeNetKeys.isFetching),
            isAddingCard: addPaymentCard.isLoading,
            isReady: isAcceptJsReady,
            onRetry: () => {
                if (paymentCardStatus.isError) {
                    paymentCardStatus.refetch();
                } else if (authorizeNetKeys.isError) {
                    authorizeNetKeys.refetch();
                }
            },
            renderForm: (onSaved) => (
                <AddCardForm
                    formId="consent-add-card-form"
                    authorizeNetCredentials={authorizeNetKeys.data}
                    useDefaultCredentials={false}
                    addPaymentCard={addPaymentCard.mutateAsync}
                    onSaved={onSaved}
                />
            ),
        };
    }

    const clientFullName = [
        consent.client_details?.first_name,
        consent.client_details?.last_name,
    ]
        .filter(Boolean)
        .join(" ");
    const clientDateOfBirth = consent.client_details?.date_of_birth ?? "";
    const initialUploadedFiles = getConsentAttachmentFiles(
        consent.consent_documents ?? []
    );

    const uploadFiles = async (fieldId: string, files: File[]) => {
        await uploadAttachments.mutateAsync({ fieldId, files });
    };

    let content: React.ReactNode;
    if (submitted) {
        content = (
            <div>
                <p className="text-base font-semibold mb-8">
                    Consent submitted
                </p>
                <p className="text-sm text-[rgba(11,19,43,0.6)] mb-16">
                    Your consent form has been received. The team will review it
                    shortly.
                </p>
                {consent.consent_documents?.map((document) => (
                    <Button
                        key={document.document_id}
                        variant="secondary"
                        mode="outline"
                        onClick={() =>
                            download(
                                document.document_id,
                                document.document_name
                            )
                        }
                    >
                        Download {document.document_name}
                    </Button>
                ))}
                <p className="mt-16 text-sm text-[rgba(11,19,43,0.6)]">
                    <Link
                        to="/login"
                        className="text-primary underline underline-offset-2"
                    >
                        Go to login
                    </Link>
                </p>
            </div>
        );
    } else if (!isDraftSaveable) {
        content = (
            <p>
                This consent form is unavailable. Please contact your provider
                for help.
            </p>
        );
    } else if (consent.form_definition) {
        content = (
            <ConsentStepper
                consentId={consent.consent_id}
                brand={consent.form_definition.brand}
                formType={consent.consent_form_type}
                variant={consent.form_definition.variant}
                clientFullName={clientFullName}
                clientDateOfBirth={clientDateOfBirth}
                pages={consent.form_definition.pages}
                initialDraftData={consent.draft_data}
                isSubmitting={action.isLoading}
                onSaveDraft={async (data) => {
                    await draft.mutateAsync(data);
                }}
                isSavingDraft={draft.isLoading}
                paymentCard={paymentCard}
                paymentCardStepConfig={consent.form_definition.paymentCardStep}
                attachmentsStepConfig={consent.form_definition.attachmentsStep}
                initialUploadedFiles={initialUploadedFiles}
                onUploadAttachment={uploadFiles}
                onSubmit={submit}
            />
        );
    } else {
        content = <p>Consent form is unavailable.</p>;
    }

    return (
        <div className="p-32">
            <Card classNames="p-24 w-full">
                <div className="mb-20 pb-16 border-b border-strokelight">
                    <h1 className="text-xl font-semibold mb-12">
                        {consent.form_definition?.label ?? "Consent form"}
                    </h1>
                    {consent.provider_details && (
                        <div>
                            <p className="text-[10px] uppercase text-[rgba(11,19,43,0.4)] mb-4">
                                Provider
                            </p>
                            <p className="text-sm font-medium">
                                {consent.provider_details.first_name}{" "}
                                {consent.provider_details.last_name}
                                {consent.provider_details.credential
                                    ? ` · ${consent.provider_details.credential}`
                                    : ""}
                            </p>
                        </div>
                    )}
                </div>
                {content}
            </Card>
        </div>
    );
}
